import { assertNotNull } from '@/helpers/typing'
import { ab } from './interface'
import {
  Experiment,
  TrialByPlan15535Variant,
  FuzzySearch15538Variant,
  NetNewQuestionnaire16167Variant,
  IosExternalLinkPayment16218Variant,
  Streaks16464Variant,
  Onboarding16689Variant,
} from './types'

import { runTextUpdates } from '@/services/ab/13448_ab_experiment'
import { Doc } from '@/models/interfaces'
import { Sentry } from '@/services/sentry'
import { IS_PRERENDERING } from '@/init/settings'
import Cookies from 'js-cookie'
import { querySelectorWait } from '@/helpers/dom'
import { isContentTranslated, waitWeglotInitialization } from '../weglot'
import { platform } from '@/services/platform'
import { platformData } from '@/services/platform.capacitor'
import { getIosStorefrontLocation } from '@/services/capacitor.storefrontLocationPlugin'

/**
 * Initialize CloudFront experiments.
 *
 * Check A/B testing cookies set by CloudFront, send backend
 * requests and update frontend a/b data storage.
 */
export async function initAB(): Promise<void> {
  await newCopy13448Experiment(Experiment.new_copy_pdf_v2_13448, '/pdf')
  await aiGuideFlow13747()
}

/**
 * Feature flag to enable/disable Google Billing. Disabled by default.
 *
 * This is not a real A/B experiment, i.e. the experiment is not activated on
 * the backend.
 */
export function googleBillingEnabled(): boolean {
  return ab.experimentVariant(Experiment.google_billing_7517) === 'yes'
}

/**
 * Apply the text changes on the public pages
 *
 * @param {Experiment} experiment - The A/B experiment containing the text changes.
 * @param {string} allowedUrlExpression - A regular expression to match allowed URLs,
 * on which the text changes should be applied.
 */
export async function newCopy13448Experiment(
  experiment: Experiment,
  allowedUrlExpression: string,
): Promise<void> {
  const pathname = window.location.pathname
  const isTranslatedPage = pathname.includes(`/es${allowedUrlExpression}/`)

  if (!pathname.includes(allowedUrlExpression) || isTranslatedPage) {
    // Only start experiment on non-translated pdf pages
    return
  }

  let result = ab.experimentVariant(experiment)

  if (result === null) {
    result = await ab.start(experiment)
  }

  if (result === 'yes') {
    runTextUpdates(experiment)
  }
}

/**
 * Get the variant of the ai_label_13537 experiment.
 * Returns boolean:
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export function aiLabel13537ExperimentVariant(): boolean {
  return ab.experimentVariant(Experiment.ai_label_13537) === 'yes'
}

/**
 * Decide wheter or not to render the ai content label component in
 * the book preview and summary pages.
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export async function aiLabel13537Experiment(): Promise<boolean> {
  let result = ab.experimentVariant(Experiment.ai_label_13537)

  if (result === null) {
    result = await ab.start(Experiment.ai_label_13537)
  }

  return result === 'yes'
}

/*
 * Show the human book cover or the fallback on public summary pages:
 - A: Show human book cover
 - B: Show fall back book cover

 Within the main app we will only apply the experiment variant to one book (the one of the visited summary page).
 */
export async function aiCover13860Experiment(): Promise<void> {
  // Don't run the experiment while generating the static pages
  if (IS_PRERENDERING) {
    return
  }

  // Selection with waiting is needed to make this work locally with Vue.
  const fallbackCover = assertNotNull(
    await querySelectorWait('.fallback-cover'),
  )
  const humanCover = document.querySelector('.book-cover__image')

  if (!humanCover) {
    // Don't start the experiment if we don't have a human cover
    fallbackCover.classList.remove('opacity_cover_13860')
    return
  }

  let useFallbackCover = false

  await waitWeglotInitialization()
  const isTranslated = isContentTranslated()

  const cfVariant13747 = Cookies.get('X-AB-AI-Guide-Flow-13747')
  const localVariant13747 = ab.experimentVariant(Experiment.ai_guide_flow_13747)
  if (cfVariant13747 || localVariant13747 || isTranslated) {
    // The `ai_guide_flow_13747` experiment was started on CloudFront
    // Set excluded variant for this experiment
    // Note that we check for `ai_guide_flow_13747` experiment, but we exclude the user
    // from the (different) `ai_cover_flow_13860` experiment.
    // This is correct, we want to avoid the case where two experiments interact with
    // each other, see #14084 for details.
    //
    // Also, we don't start the ai_cover_flow_13860 experiment for the translated pages.
    await ab.start(Experiment.ai_cover_flow_13860, '_EXCLUDED_')
    // For translated pages we use the fallback cover.
    useFallbackCover = isTranslated
  } else {
    // Get the variant ("A" - human cover, "B" - automatic)
    let experimentVariant = ab.experimentVariant(Experiment.ai_cover_flow_13860)

    if (experimentVariant === null) {
      // Start the experiment
      experimentVariant = await ab.start(Experiment.ai_cover_flow_13860)
    }
    useFallbackCover = experimentVariant === 'B'
  }

  if (useFallbackCover) {
    // Hide human cover
    humanCover.classList.add('hide_cover_13860')
    // Show fallback cover
    fallbackCover.classList.remove('hide_cover_13860')
  }
  humanCover.classList.remove('opacity_cover_13860')
  fallbackCover.classList.remove('opacity_cover_13860')
}

/*
 * Check if we should show the fallback cover as part of the experiment ai_cover_flow_13860
 */
export function useFallbackCover_13860(doc: Doc): boolean {
  const experimentVariant = ab.experimentVariant(Experiment.ai_cover_flow_13860)
  return experimentVariant === 'B' && doc.user?.is_first_doc
}

/*
 * Send a sentry message if we have an unexpected case.
 * The user is in the experiment, this is experiment book, but we do not have human cover.
 * In this case the book should not be in the experiment.
 */
export function checkCoverFlow_13860(doc: Doc): void {
  const experimentVariant = ab.experimentVariant(Experiment.ai_cover_flow_13860)

  if (
    experimentVariant !== null &&
    doc.user?.is_first_doc &&
    !doc.cover_image
  ) {
    Sentry.captureMessage(
      `Unexpected case in experiment ${Experiment.ai_cover_flow_13860}: Book [${doc.url_slug}] doesn't have a cover but is in the experiment.`,
    )
  }
}

/*
 * Read experiment cookies set by CloudFront and transfer the experiment assignment to the local storage
 */
export async function aiGuideFlow13747(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-AI-Guide-Flow-13747')
  const localVariant = ab.experimentVariant(Experiment.ai_guide_flow_13747)

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.ai_guide_flow_13747, cfVariant)
  }
}

export async function trialByPlan15535Experiment(): Promise<TrialByPlan15535Variant> {
  // We have experiment variant in the local storage.
  let result = ab.experimentVariant(
    Experiment.exp_trial_by_plan_15535,
  ) as TrialByPlan15535Variant

  const isAndroid = await platform.isAndroid()

  // We don't start the experiment on Android
  if (isAndroid) {
    return null
  }

  // Not Android, start the experiment
  if (result === null) {
    result = (await ab.start(
      Experiment.exp_trial_by_plan_15535,
    )) as TrialByPlan15535Variant
  }

  return result
}

export function trialByPlan15535Variant(): string | null {
  return ab.experimentVariant(
    Experiment.exp_trial_by_plan_15535,
  ) as TrialByPlan15535Variant
}

/**
 * The Fuzzy Search experiment.
 *
 * no - Use the current "literal" search, control variant.
 * yes - Use the fuzzy search, treatment variant
 */
export async function fuzzySearch15538Experiment(): Promise<FuzzySearch15538Variant> {
  let result = ab.experimentVariant(Experiment.exp_fuzzy_search_15538)

  if (result === null) {
    result = await ab.start(Experiment.exp_fuzzy_search_15538)
  }

  return result as FuzzySearch15538Variant
}

/**
 * Get the variant for the exp_fuzzy_search_15538 experiment.
 */
export function fuzzySearch15538Variant(): FuzzySearch15538Variant {
  return ab.experimentVariant(
    Experiment.exp_fuzzy_search_15538,
  ) as FuzzySearch15538Variant
}

/**
 * Get the variant for the exp_net_new_questionnaire_16167 experiment.
 */
export function netNewQuestionnaire16167Variant(): NetNewQuestionnaire16167Variant {
  return ab.experimentVariant(
    Experiment.exp_net_new_questionnaire_16167,
  ) as NetNewQuestionnaire16167Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export async function iosExternalLinkPayment16218Experiment(): Promise<IosExternalLinkPayment16218Variant> {
  await platformData.init()
  const appBuild = platformData.appBuild
  // We need to get the storefront location for the experiment
  // and it's not available in builds < 60.
  if (appBuild < 60) {
    return null
  }
  const storefrontLocation = await getIosStorefrontLocation()
  // External payments are allowed in the US storefront only.
  if (storefrontLocation !== 'USA') {
    return null
  }

  // Check variant in the local storage
  let result = ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  )

  // Start the experiment
  if (result === null) {
    result = await ab.start(Experiment.exp_ios_external_link_payment_16218)
  }

  return result as IosExternalLinkPayment16218Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export function iosExternalLinkPayment16218Variant(): IosExternalLinkPayment16218Variant {
  return ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  ) as IosExternalLinkPayment16218Variant
}

/**
 * Start the streaks_16464 experiment.
 *
 * A - Control, current logic without Streaks.
 * B - Version with the Streaks feature.
 */
export async function streaks16464Experiment(): Promise<Streaks16464Variant> {
  let result = ab.experimentVariant(Experiment.exp_streaks_16464)

  if (result === null) {
    result = await ab.start(Experiment.exp_streaks_16464)
  }

  return result as Streaks16464Variant
}

/**
 * Get the variant for the streaks_16464 experiment.
 */
export function streaks16464Variant(): Streaks16464Variant {
  return ab.experimentVariant(
    Experiment.exp_streaks_16464,
  ) as Streaks16464Variant
}

/**
 * Get the variant for the exp_onboarding_v5_reduced_animations_16689 experiment.
 */
export function onboardingV5_16689Variant(): Onboarding16689Variant {
  return ab.experimentVariant(
    Experiment.exp_onboarding_v5_reduced_animations_16689,
  ) as Onboarding16689Variant
}

/**
 * Get the treatment variant for the exp_onboarding_v5_reduced_animations_16689 experiment.
 */
export function onboardingV5_16689Treatment(): boolean {
  const variant = ab.experimentVariant(
    Experiment.exp_onboarding_v5_reduced_animations_16689,
  ) as Onboarding16689Variant
  return variant !== null && ['B', 'C'].includes(variant)
}

/**
 * Check if we should show the new onboarding items.
 * - Show the new onboarding items if the netNewQuestionnaire16167Variant is 'B'
 * - Show the new onboarding items if the onboardingV5ReducedAnimations16689Variant is 'B' or 'C'
 */
export function showNewOnboardingItems(): boolean {
  return (
    netNewQuestionnaire16167Variant() === 'B' || onboardingV5_16689Treatment()
  )
}
